import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/components/ToastProvider';
import Button from '@/components/Button';
import BasicInput from '@/components/BasicInput';
import EmailVerification from '@/components/EmailVerification';
import ResetPasswordDialog from '@/components/ResetPasswordDialog';
import * as auth from '@/api/auth';
import { validateEmail } from '@/utils/validationUtils';

const ForgotPasswordPage = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [errors, setErrors] = useState({});

  const handleSendVerification = async (e) => {
    if (e) e.preventDefault();
    
    // Validate email
    if (!email) {
      setErrors({ email: t('auth.signup.errors.email_required') });
      return;
    } else if (!validateEmail(email)) {
      setErrors({ email: t('auth.signup.errors.email_invalid') });
      return;
    }

    setIsLoading(true);
    try {
      await auth.sendEmailVerificationForReset(email);
      setIsEmailSent(true);
      setErrors({});
    } catch (error) {
      console.error('Verification request failed:', error);
      setErrors({
        email: t('auth.forgot_password.errors.email_not_exist') || 'Email does not exist'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode) {
      setErrors({
        verificationCode: t('auth.signup.verification.code_required')
      });
      return;
    }

    // Clear any previous verification code errors
    setErrors({});
    
    // Instead of verifying the code here, we'll show the reset password dialog
    // The actual verification will happen when the user submits the new password
    setShowResetDialog(true);
  };

  const handleResendCode = async () => {
    setIsLoading(true);
    try {
      await auth.sendEmailVerificationForReset(email);
      toast(t('auth.signup.verification.resend_success'));
    } catch (error) {
      console.error('Resend code failed:', error);
      toast(t('auth.signup.verification.resend_error'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseResetDialog = (result) => {
    setShowResetDialog(false);
    
    // Handle verification code error passed back from the dialog
    if (result && result.verificationCodeError === 'invalid_verification_code') {
      setErrors({ 
        verificationCode: t('auth.forgot_password.errors.invalid_verification_code')
      });
    }
  };

  if (isEmailSent) {
    return (
      <div className="container py-8">
        <EmailVerification 
          email={email}
          verificationCode={verificationCode}
          setVerificationCode={setVerificationCode}
          onVerify={handleVerifyCode}
          onResend={handleResendCode}
          errors={errors}
          isLoading={isLoading}
        />
        
        {showResetDialog && (
          <ResetPasswordDialog 
            onClose={handleCloseResetDialog}
            email={email}
            verificationCode={verificationCode}
          />
        )}
      </div>
    );
  }

  return (
    <div className="container py-8 flex flex-col items-center justify-center">
      <div className="w-[360px] mx-auto flex-col justify-start items-center gap-8 mb-[200px]">
        <div className="self-stretch flex flex-col justify-start items-center gap-4">
          <div className="self-stretch text-center justify-center text-[#111111] text-2xl font-bold leading-normal">
            {t('auth.forgot_password.title')}
          </div>
          <div className="self-stretch justify-center text-[#666666] text-base font-normal">
            {t('auth.forgot_password.description')}
          </div>
        </div>
        
        <div className="self-stretch flex flex-col justify-start items-start gap-6 mt-6">
          <form onSubmit={handleSendVerification} className="self-stretch flex flex-col justify-start items-start gap-1">
            <div className="self-stretch inline-flex justify-start items-start gap-3">
              <div className="flex-1 flex justify-start items-center gap-1">
                <div className="justify-start text-[#111111] text-sm font-normal leading-[21px]">
                  {t('auth.login.email')}
                </div>
              </div>
            </div>
            <BasicInput
              type="email"
              placeholder={t('auth.login.email_placeholder')}
              containerClassName="self-stretch"
              className="bg-[#f7f7f7] rounded-md outline outline-1 outline-offset-[-1px] outline-[#dddddd]"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={errors.email}
              required
              maxLength={40}
            />
          </form>
          
          <Button 
            variant="primary" 
            size="xl" 
            className="self-stretch"
            onClick={handleSendVerification}
            disabled={isLoading || !email}
          >
            {isLoading ? t('common.loading') : (t('auth.forgot_password.send_code') || '发送验证码')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage; 